import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { LayoutDashboard, ClipboardList, FileSpreadsheet, Receipt, Truck, Users, LogOut, Wallet, Boxes, Upload, Camera, BookOpen, NotebookPen, TrendingUp, Scale, KeyRound, FileText } from "lucide-react";
import useTallyShortcuts, { SHORTCUT_HINTS } from "../../hooks/useTallyShortcuts";
import InstallPWA from "../../components/InstallPWA";

export default function EmployeeLayout() {
    const { user, logout } = useAuth();
    const nav = useNavigate();
    useTallyShortcuts();
    if (!user || user.role !== "employee") return <div className="p-12 text-center">Employee login required</div>;

    const caps = user.capabilities || [];
    const NAV = [{ to: "/employee", icon: LayoutDashboard, label: "Hub", end: true }];

    // "Order status" tab — visible to MR (field tracking), Accounts & Billing
    // (dispatch follow-up), but NOT to standalone packing/stock employees.
    const showOrderStatus = caps.includes("mr") || caps.includes("accounts");
    if (showOrderStatus) {
        NAV.push({ to: "/employee/order-status", icon: Truck, label: "Order status" });
    }

    if (caps.includes("mr")) {
        NAV.push({ to: "/employee/mr/place-order", icon: ClipboardList, label: "Place order" });
        NAV.push({ to: "/employee/crm", icon: Users, label: "CRM" });
        NAV.push({ to: "/employee/mr/drs", icon: FileSpreadsheet, label: "Daily Report" });
        NAV.push({ to: "/employee/mr/dues", icon: Wallet, label: "Dues lookup" });
        NAV.push({ to: "/employee/mr/expenses", icon: Camera, label: "Market expenses" });
        NAV.push({ to: "/employee/distributor-hub", icon: TrendingUp, label: "Distributor Hub" });
        NAV.push({ to: "/employee/packing-slips", icon: FileText, label: "Packing slips" });
    }
    if (caps.includes("packing")) NAV.push({ to: "/employee/packing", icon: Truck, label: "Packing & Dispatch" });
    if (caps.includes("stock")) NAV.push({ to: "/employee/stock", icon: Boxes, label: "Stock update" });
    if (caps.includes("accounts")) {
        NAV.push({ to: "/employee/accounts/invoices", icon: Receipt, label: "Invoices" });
        NAV.push({ to: "/employee/accounts/uploads", icon: Upload, label: "External invoices" });
        NAV.push({ to: "/employee/accounts/customers", icon: Users, label: "Customer master" });
        NAV.push({ to: "/employee/accounts/dues", icon: Wallet, label: "All dues" });
        NAV.push({ to: "/employee/distributor-hub", icon: TrendingUp, label: "Distributor Hub" });
        NAV.push({ to: "/employee/packing-slips", icon: FileText, label: "Packing slips" });
        // Tally-style hub
        NAV.push({ to: "/employee/accounts/daybook", icon: BookOpen, label: "Day Book", section: "accounts-hub" });
        NAV.push({ to: "/employee/accounts/sales-register", icon: FileSpreadsheet, label: "Sales Register", section: "accounts-hub" });
        NAV.push({ to: "/employee/accounts/outstanding", icon: Wallet, label: "Outstanding", section: "accounts-hub" });
        NAV.push({ to: "/employee/accounts/stock-summary", icon: Boxes, label: "Stock Summary", section: "accounts-hub" });
        NAV.push({ to: "/employee/accounts/journal", icon: NotebookPen, label: "Journal", section: "accounts-hub" });
        NAV.push({ to: "/employee/accounts/pnl", icon: TrendingUp, label: "Profit & Loss", section: "accounts-hub" });
        NAV.push({ to: "/employee/accounts/balance-sheet", icon: Scale, label: "Balance Sheet", section: "accounts-hub" });
        NAV.push({ to: "/employee/accounts/gst-returns", icon: Receipt, label: "GST Returns", section: "accounts-hub" });
    }

    // Dedupe NAV by `to` (an employee can have both mr + accounts which both push packing-slips)
    const seen = new Set();
    const dedupedNAV = NAV.filter((n) => { if (seen.has(n.to)) return false; seen.add(n.to); return true; });

    const handleLogout = () => { logout(); nav("/login"); };

    return (
        <div className="min-h-screen bg-stone-100" data-testid="employee-layout">
            {/* Top bar — always-visible Sign out */}
            <header className="sticky top-0 z-30 bg-stone-900 text-stone-50 border-b border-stone-800">
                <div className="max-w-[1500px] mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <span className="w-7 h-7 grid place-items-center bg-sky-700 text-stone-50 font-serif text-sm">H</span>
                        <div className="leading-tight">
                            <div className="font-serif text-base">HPL Employee Portal</div>
                            <div className="text-[9px] tracking-[0.3em] uppercase text-stone-400">{user.designation_name || "Staff"} · {user.name}</div>
                        </div>
                    </div>
                    <button
                        onClick={handleLogout}
                        className="text-xs text-stone-200 hover:text-white border border-stone-700 hover:border-sky-600 hover:bg-stone-800 px-3 py-1.5 inline-flex items-center gap-1 transition-colors"
                        data-testid="employee-logout"
                    >
                        <LogOut className="w-3.5 h-3.5" />Sign out
                    </button>
                </div>
            </header>

            <div className="max-w-[1500px] mx-auto grid grid-cols-12">
                {/* Sidebar — flex col with logout pinned to bottom of viewport */}
                <aside className="col-span-3 lg:col-span-2 bg-white border-r border-stone-200 sticky top-14 self-start h-[calc(100vh-3.5rem)] flex flex-col">
                    <nav className="flex-1 overflow-y-auto p-4 space-y-1">
                        {dedupedNAV.map((n, i) => {
                            const prev = dedupedNAV[i - 1];
                            const startsHub = n.section === "accounts-hub" && prev?.section !== "accounts-hub";
                            return (
                                <React.Fragment key={n.to}>
                                    {startsHub && (
                                        <div className="pt-3 pb-1 px-3 text-[9px] tracking-[0.25em] uppercase text-stone-500 border-t border-stone-200 mt-2">
                                            Accounts Hub · Tally-style
                                        </div>
                                    )}
                                    <NavLink to={n.to} end={n.end}
                                        className={({ isActive }) => `flex items-center gap-3 px-3 py-2 text-sm transition-colors ${isActive ? "bg-sky-700 text-stone-50" : "text-stone-700 hover:bg-stone-100"}`}
                                        data-testid={`emp-nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                                        <n.icon className="w-4 h-4" />{n.label}
                                    </NavLink>
                                </React.Fragment>
                            );
                        })}
                    </nav>
                    <div className="border-t border-stone-200 p-4 bg-white">
                        <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-1">Signed in as</div>
                        <div className="text-xs text-stone-700 truncate" title={user.email}>{user.email}</div>
                        {caps.includes("mr") && (
                            <div className="mt-3 px-2 py-2 bg-sky-50 border-l-2 border-sky-700">
                                <div className="text-[9px] uppercase tracking-widest text-sky-700 font-semibold">Territory</div>
                                {user.territory ? (
                                    <div className="text-xs text-stone-800 mt-0.5 leading-snug" data-testid="emp-territory-chip">{user.territory}</div>
                                ) : (
                                    <div className="text-[11px] text-amber-700 mt-0.5 leading-snug" data-testid="emp-territory-none">
                                        Not assigned — ask admin to set this in Admin → Users → Edit you.
                                    </div>
                                )}
                            </div>
                        )}
                        <NavLink
                            to="/account/change-password"
                            className="mt-3 w-full text-xs text-stone-700 hover:text-sky-700 flex items-center justify-center gap-2 px-3 py-2 border border-stone-300 hover:border-sky-700 transition-colors"
                            data-testid="employee-change-password"
                        >
                            <KeyRound className="w-3.5 h-3.5" /> Change password
                        </NavLink>
                        <button
                            onClick={handleLogout}
                            className="mt-3 w-full text-xs text-stone-700 hover:text-sky-700 flex items-center justify-center gap-2 px-3 py-2 border border-stone-300 hover:border-sky-700 transition-colors"
                            data-testid="employee-logout-sidebar"
                        >
                            <LogOut className="w-3.5 h-3.5" /> Sign out
                        </button>
                    </div>
                </aside>
                <main className="col-span-9 lg:col-span-10 p-6 lg:p-10">
                    <Outlet />
                    {(user.capabilities || []).includes("accounts") && (
                        <div className="mt-10 pt-4 border-t border-stone-200 flex flex-wrap gap-x-4 gap-y-1 text-[10px] text-stone-500" data-testid="shortcut-hints">
                            <span className="uppercase tracking-widest text-stone-400">Shortcuts</span>
                            {SHORTCUT_HINTS.map((s) => (
                                <span key={s.key} className="font-mono">
                                    <kbd className="px-1 py-0.5 bg-stone-100 border border-stone-200 text-stone-700">{s.key}</kbd>
                                    <span className="ml-1">{s.label}</span>
                                </span>
                            ))}
                        </div>
                    )}
                </main>
            </div>
            <InstallPWA />
        </div>
    );
}
